import { useGetEmployees } from '@agos/api-client/hr'
import { useGetAuditLogs } from '@agos/api-client/reporting'
import { T, tt } from '@agos/shared'
import {
  DataTable,
  FilterBar,
  FilterSelect,
  PageHeader,
  Spinner,
  StatusBadge,
  cn,
  type Column,
} from '@agos/ui'
import { ChevronDown, ChevronUp, History } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  EMPTY_AUDIT_FILTER,
  actionColor,
  actionLabel,
  auditPaging,
  auditQuery,
  detailPairs,
  entityLabel,
  entityOptions,
  formatCount,
  formatStamp,
  matchesAuditSearch,
  roleLabel,
  type AuditFilter,
  type AuditListing,
  type AuditRow,
} from './log-viewer'

/**
 * `/audit-log` — who changed what. 79 700 rows in the restored data.
 *
 * **FB-108's half of the story.** This endpoint answers a `total` and takes `limit`/`offset`, so the
 * page can say *1–50 z 79 700* rather than *page 1*. Its sibling `/system-logs` can do neither, and
 * that page says so.
 *
 * Legacy lists sixteen `status_change:*` / `confirm_status:*` labels by hand, so a status the kitchen
 * gains tomorrow renders as a raw key; `actionLabel` splits on the colon instead.
 *
 * Ported from legacy `pages/audit-log.tsx` (364 lines). Exported as `AuditLogContent` too, because
 * `/system-logs` shows it as a tab — as legacy does.
 */

export function AuditLogPage() {
  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Audit log')}
        description={tt('Kto čo zmenil, kedy a z akej roly.')}
        icon={<History className="w-5 h-5" />}
      />
      <AuditLogContent />
    </div>
  )
}

interface EmployeeOption {
  id?: number
  name?: string
}

export function AuditLogContent() {
  const [filter, setFilter] = useState<AuditFilter>(EMPTY_AUDIT_FILTER)
  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = useState<number | null>(null)

  const { data, isLoading } = useGetAuditLogs(auditQuery(filter, page))
  const listing = (data ?? {}) as AuditListing
  const rows = useMemo(() => listing.rows ?? [], [listing.rows])
  const shown = rows.filter((row) => matchesAuditSearch(row, filter.search))
  const paging = auditPaging(listing.total ?? 0, page, rows.length)

  const { data: staffData } = useGetEmployees()
  const staff = useMemo<EmployeeOption[]>(() => staffData ?? [], [staffData])

  const set = <K extends keyof AuditFilter>(key: K, value: AuditFilter[K]) => {
    setFilter((current) => ({ ...current, [key]: value }))
    // A filter change re-decides which page is which, so page one is the only honest place to land —
    // and since FB-108 the search is a filter like any other, because the server applies it.
    setPage(1)
  }

  // FB-109: legacy's dropdown is its five-entry label map, so `stock_item`, `stock_receipt` and
  // `order_tab` — 23 rows — cannot be selected at all.
  const entityTypes = useMemo(() => entityOptions(rows), [rows])

  const columns: Column<AuditRow>[] = useMemo(
    () => [
      {
        key: 'createdAt',
        header: tt('Kedy'),
        render: (row) => (
          <span className="text-[11px] text-muted-foreground whitespace-nowrap tabular-nums">
            {formatStamp(row.createdAt)}
          </span>
        ),
      },
      {
        key: 'employee',
        header: tt('Kto'),
        render: (row) => (
          <div className="min-w-0">
            <p className="text-xs font-semibold truncate">{row.employeeName ?? '-'}</p>
            <p className="text-[10px] text-muted-foreground">{roleLabel(row.role)}</p>
          </div>
        ),
      },
      {
        key: 'action',
        header: tt('Čo'),
        render: (row) => (
          <StatusBadge label={actionLabel(row.action)} color={actionColor(row.action)} />
        ),
      },
      {
        key: 'entity',
        header: tt('Na čom'),
        render: (row) => (
          <span className="text-xs">
            {entityLabel(row.entityType)}
            {row.entityId !== null && row.entityId !== undefined && (
              <span className="text-muted-foreground"> #{row.entityId}</span>
            )}
          </span>
        ),
      },
      {
        key: 'detail',
        header: '',
        render: (row) => {
          const pairs = detailPairs(row.detail)
          if (pairs.length === 0) return <span className="text-[10px] text-muted-foreground">-</span>
          const open = expanded === row.id
          return (
            <button
              type="button"
              onClick={() => setExpanded(open ? null : (row.id ?? null))}
              aria-label={`${tt('Podrobnosti')} ${row.id ?? ''}`}
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            >
              {pairs.length} <T>polí</T>
              {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )
        },
      },
    ],
    [expanded],
  )

  return (
    <div className="space-y-3">
      <FilterBar
        search={{
          value: filter.search,
          onChange: (value) => set('search', value),
          placeholder: tt('Hľadať v celom denníku...'),
        }}
        filters={
          <>
            <FilterSelect
              value={filter.employeeId}
              label={tt('Zamestnanec')}
              onChange={(value) => set('employeeId', value)}
              options={[
                { value: 'all', label: tt('Všetci') },
                ...staff.map((member) => ({
                  value: String(member.id ?? ''),
                  label: member.name ?? '',
                })),
              ]}
            />
            <FilterSelect
              value={filter.entityType}
              label={tt('Typ')}
              onChange={(value) => set('entityType', value)}
              options={[{ value: 'all', label: tt('Všetko') }, ...entityTypes]}
            />
            <input
              type="date"
              value={filter.dateFrom}
              onChange={(event) => set('dateFrom', event.target.value)}
              aria-label={tt('Od dátumu')}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
            />
            <input
              type="date"
              value={filter.dateTo}
              onChange={(event) => set('dateTo', event.target.value)}
              aria-label={tt('Do dátumu')}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
            />
          </>
        }
      />

      {/* The slice, not the page number - 79 700 rows is 1 594 pages. */}
      <div className="flex flex-wrap items-center gap-2 text-xs" data-audit-paging>
        <span className="text-muted-foreground">
          {formatCount(paging.from)}–{formatCount(paging.to)} <T>z</T>{' '}
          <strong>{formatCount(paging.total)}</strong>
          {filter.search.trim() !== '' && (
            <>
              {' '}
              · <T>hľadané v celom denníku</T>
            </>
          )}
        </span>
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => setPage((current) => Math.max(1, current - 1))}
          disabled={page <= 1}
          className="px-3 py-1.5 rounded-lg border border-border font-semibold disabled:opacity-40"
        >
          <T>Novšie</T>
        </button>
        <span className="text-muted-foreground tabular-nums">
          {formatCount(page)} / {formatCount(paging.pages)}
        </span>
        <button
          type="button"
          onClick={() => setPage((current) => Math.min(paging.pages, current + 1))}
          disabled={page >= paging.pages}
          className="px-3 py-1.5 rounded-lg border border-border font-semibold disabled:opacity-40"
        >
          <T>Staršie</T>
        </button>
      </div>

      {filter.search.trim() !== '' && (
        <p className="text-[11px] text-muted-foreground" data-search-scope>
          {/* FB-108. It said the search covered only the loaded page, because it did. */}
          <T>
            Hľadá sa vo všetkých záznamoch - v mene, akcii, type, čísle aj v podrobnostiach.
          </T>
        </p>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <div data-audit-rows>
          <DataTable<AuditRow>
            columns={columns}
            data={shown}
            rowKey={(row) => row.id ?? 0}
            pageSize={50}
            compact
            emptyMessage={tt('Žiadne záznamy')}
            emptyIcon={<History className="w-10 h-10 mb-3 opacity-30" />}
          />
        </div>
      )}

      {expanded !== null && (
        <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-1" data-audit-detail>
          <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
            <T>Podrobnosti záznamu</T> #{expanded}
          </p>
          {detailPairs(rows.find((row) => row.id === expanded)?.detail).map(([key, value]) => (
            <p key={key} className="text-xs">
              <span className="text-muted-foreground">{key}</span>{' '}
              <span className={cn('font-mono')}>{value}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
